import React from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';

interface ToggleSettingProps {
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  label: string;
  description: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  rightElement?: React.ReactNode;
  onClick?: () => void;
  sx?: React.CSSProperties | Record<string, any>;
}

interface SettingsSectionProps {
  title: string;
  children: React.ReactNode;
  badge?: string;
}

interface CopyButtonProps {
  text: string;
  onCopy?: () => void;
}

export const CopyButton: React.FC<CopyButtonProps> = ({ text, onCopy }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopy?.();
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCopy}
            disabled={copied}
            className="h-8 w-8 p-0 rounded-lg bg-background border hover:bg-secondary hover:text-secondary-foreground"
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {copied
            ? t('settings.api.visibleToken.tooltips.copied')
            : t('settings.api.visibleToken.tooltips.copyToken')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const ToggleSetting: React.FC<ToggleSettingProps> = ({
  checked,
  onChange,
  label,
  description,
  icon,
  children,
  rightElement,
  onClick,
  sx,
}) => (
  <div
    className="py-3 flex flex-col gap-4"
    style={sx}
    {...(onClick && {
      onClick,
      onKeyDown: (e) => e.key === 'Enter' && onClick(),
      role: 'button',
      tabIndex: 0,
    })}
  >
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
      <div className="flex-1 w-full sm:mr-6">
        <div className="flex items-center">
          {icon && (
            <div className="text-muted-foreground flex items-center mr-3 w-7">
              {React.cloneElement(icon as React.ReactElement, {
                className: 'h-5 w-5',
              })}
            </div>
          )}
          <div className="flex-1">
            <div className="font-medium mb-1">{label}</div>
            <div className="text-sm text-muted-foreground">{description}</div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 w-full sm:w-auto justify-end sm:justify-start">
        {rightElement}
        {!children && !rightElement && (
          <Switch
            checked={checked}
            onCheckedChange={(isChecked: boolean) =>
              onChange({
                target: { checked: isChecked },
              } as React.ChangeEvent<HTMLInputElement>)
            }
          />
        )}
      </div>
    </div>
    {children && <div className="pl-0 sm:pl-10">{children}</div>}
  </div>
);

export const SettingsSection: React.FC<SettingsSectionProps> = ({
  title,
  children,
  badge,
}) => (
  <div>
    <div className="border-b border-border mb-2">
      <h3 className="py-3 font-medium text-foreground text-xl tracking-tight flex items-center">
        {title}
        {badge && (
          <Badge variant="default" className="ml-2 h-5 text-xs">
            {badge}
          </Badge>
        )}
      </h3>
    </div>
    <div>{children}</div>
  </div>
);

export const renderSetting = (
  _label: string,
  value: boolean | string,
  onChange: (newValue: boolean | string) => void,
) => {
  if (typeof value === 'boolean') {
    return (
      <Switch
        checked={value}
        onCheckedChange={(checked: boolean) => onChange(checked)}
      />
    );
  }
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="ml-2"
    />
  );
};

export type SubSettings =
  | 'audioSettings'
  | 'videoSettings'
  | 'recordingControlSettings'
  | 'uiSettings'
  | 'otherSettings';

export const recordingSubSettings: SubSettings[] = [
  'audioSettings',
  'videoSettings',
  'recordingControlSettings',
  'otherSettings',
];
export const pluginSubSettings: SubSettings[] = recordingSubSettings.concat([
  'uiSettings',
]);

export default renderSetting;
